import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Descriptions, Drawer, Space, Table, Tag, Typography } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import { listAuditLogs } from "../services/auditApi";

const ACTION_LABELS = {
  CREATE: "Tạo mới",
  UPDATE: "Cập nhật",
  VOID: "Hủy giao dịch",
  CLOSE: "Chốt đối soát",
  REOPEN: "Mở lại đối soát",
  ACTIVATE: "Kích hoạt",
  DEACTIVATE: "Ngừng sử dụng",
  LOGIN_SUCCESS: "Đăng nhập thành công",
  LOGIN_FAILED: "Đăng nhập thất bại",
};

function safeJson(value) {
  if (!value) return "—";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch (_) {
    return String(value);
  }
}

function parseAuditData(value) {
  if (!value) return {};
  try {
    return JSON.parse(value);
  } catch (_) {
    return {};
  }
}

function actorName(row) {
  if (row.actorDisplayName) return row.actorDisplayName;
  if (row.actorUsername) return row.actorUsername;
  if (!row.actorId) return "Hệ thống";
  return row.actorId;
}

function auditDescription(row) {
  const actor = actorName(row);
  const data = {
    ...parseAuditData(row.beforeData),
    ...parseAuditData(row.afterData),
  };
  const action = {
    CREATE: "tạo",
    UPDATE: "cập nhật",
    VOID: "hủy",
    CLOSE: "chốt",
    REOPEN: "mở lại",
    ACTIVATE: "kích hoạt",
    DEACTIVATE: "ngừng sử dụng",
  }[row.action] || (ACTION_LABELS[row.action] || row.action || "thực hiện thao tác với").toLowerCase();

  let subject;
  switch (row.entityType) {
    case "BANK_TRANSACTION": {
      const direction = data.type === "IN" ? "nhận tiền" : data.type === "OUT" ? "chi tiền" : "ngân hàng";
      subject = `giao dịch ${direction}${data.transactionCode ? ` (${data.transactionCode})` : ""}`;
      break;
    }
    case "BANK_ACCOUNT":
      subject = `tài khoản ngân hàng${data.internalCode ? ` ${data.internalCode}` : ""}`;
      break;
    case "USER": {
      const target = data.displayName || data.username;
      subject = `người dùng${target ? ` “${target}”` : ""}`;
      break;
    }
    case "USER_BANK_ACCOUNT_ACCESS":
      subject = "phân quyền tài khoản ngân hàng";
      break;
    case "TRANSACTION_ATTACHMENT":
      subject = `biên lai${data.originalFilename ? ` “${data.originalFilename}”` : ""}`;
      break;
    case "DAILY_RECONCILIATION":
      subject = "phiên đối soát số dư";
      break;
    default:
      subject = row.entityType ? `dữ liệu ${row.entityType}` : "dữ liệu hệ thống";
  }

  const reason = row.reason ? ` do ${row.reason}` : "";
  return `Người dùng “${actor}” đã ${action} ${subject}${reason}.`;
}

function ActorDisplay({ row, showId = false }) {
  const name = actorName(row);
  const secondary = row.actorDisplayName && row.actorUsername && row.actorDisplayName !== row.actorUsername
    ? row.actorUsername
    : null;
  return (
    <Space direction="vertical" size={0}>
      <Typography.Text>{name}</Typography.Text>
      {secondary && <Typography.Text type="secondary">@{secondary}</Typography.Text>}
      {showId && row.actorId && <Typography.Text type="secondary" copyable>{row.actorId}</Typography.Text>}
    </Space>
  );
}

export default function AuditLogs() {
  const [pageData, setPageData] = useState({ content: [], page: 0, size: 20, totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const sequence = useRef(0);

  async function load(page = 0, size = pageData.size || 20) {
    const request = ++sequence.current;
    setLoading(true);
    setError("");
    try {
      const result = await listAuditLogs({ page, size });
      if (request === sequence.current) setPageData(result);
    } catch (loadError) {
      if (request === sequence.current) setError(loadError.message || "Không thể tải nhật ký hoạt động");
    } finally {
      if (request === sequence.current) setLoading(false);
    }
  }

  useEffect(() => {
    load(0, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    { title: "Thời gian", dataIndex: "createdAt", width: 180, render: (value) => new Date(value).toLocaleString("vi-VN") },
    { title: "Người thực hiện", key: "actor", render: (_, row) => <ActorDisplay row={row} /> },
    { title: "Hành động", dataIndex: "action", render: (value) => <Tag color="blue">{ACTION_LABELS[value] || value}</Tag> },
    { title: "Đối tượng", render: (_, row) => `${row.entityType || "—"}${row.entityId ? ` · ${row.entityId}` : ""}` },
    { title: "Lý do", dataIndex: "reason", responsive: ["md"], render: (value) => value || "—" },
    { title: "IP", dataIndex: "ipAddress", responsive: ["lg"], render: (value) => value || "—" },
    { title: "", key: "detail", render: (_, row) => <Button type="link" icon={<EyeOutlined />} onClick={() => setSelected(row)}>Chi tiết</Button> },
  ];

  return (
    <div className="management-page">
      <div className="page-heading">
        <div>
          <Typography.Title level={2}>Nhật ký hoạt động</Typography.Title>
          <Typography.Text type="secondary">Dấu vết thay đổi trên hệ thống, sắp xếp mới nhất trước.</Typography.Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={() => load(pageData.page, pageData.size)}>Tải lại</Button>
      </div>
      {error && <Alert className="page-alert" type="error" showIcon message={error} action={<Button size="small" onClick={() => load(pageData.page, pageData.size)}>Thử lại</Button>} />}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={pageData.content}
        loading={loading}
        locale={{ emptyText: "Chưa có hoạt động nào" }}
        scroll={{ x: 900 }}
        pagination={{
          current: pageData.page + 1,
          pageSize: pageData.size,
          total: pageData.totalElements,
          showSizeChanger: true,
          showTotal: (total) => `${total} hoạt động`,
        }}
        onChange={(pagination) => load(pagination.current - 1, pagination.pageSize)}
      />
      <Drawer title="Chi tiết hoạt động" width={640} open={Boolean(selected)} onClose={() => setSelected(null)} destroyOnClose>
        {selected && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Alert
              type="info"
              showIcon
              message="Mô tả hoạt động"
              description={auditDescription(selected)}
            />
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Người thực hiện"><ActorDisplay row={selected} /></Descriptions.Item>
              <Descriptions.Item label="ID người dùng">{selected.actorId || "—"}</Descriptions.Item>
              <Descriptions.Item label="Hành động">{ACTION_LABELS[selected.action] || selected.action}</Descriptions.Item>
              <Descriptions.Item label="Đối tượng">{selected.entityType} · {selected.entityId}</Descriptions.Item>
              <Descriptions.Item label="Lý do">{selected.reason || "—"}</Descriptions.Item>
              <Descriptions.Item label="IP">{selected.ipAddress || "—"}</Descriptions.Item>
              <Descriptions.Item label="Thiết bị">{selected.userAgent || "—"}</Descriptions.Item>
              <Descriptions.Item label="Thời gian">{new Date(selected.createdAt).toLocaleString("vi-VN")}</Descriptions.Item>
            </Descriptions>
            <section><Typography.Title level={5}>Trước thay đổi</Typography.Title><pre className="json-view">{safeJson(selected.beforeData)}</pre></section>
            <section><Typography.Title level={5}>Sau thay đổi</Typography.Title><pre className="json-view">{safeJson(selected.afterData)}</pre></section>
          </Space>
        )}
      </Drawer>
    </div>
  );
}

export { actorName, auditDescription, safeJson };
